// 表单管理服务 - 与后端API集成
import { cookieManager } from '../utils/cookieManager.js';
import {
  isValidObjectId
} from '../utils/objectIdManager.js';
import formDataStorage from '../utils/formDataStorage.js';
import { getAllFormStatuses } from '../utils/formValidation.js';
import { getFormPagesForVisaType } from '../utils/formConfig.js';
import { getApiBaseUrl } from '../utils/apiConfig.js';

class FormManagementService {
  constructor() {
    // 后端API基础URL - 默认指向生产环境服务器
    this.baseURL = getApiBaseUrl();

    // 测试API连接
    this.testApiConnection().then(success => {
      if (!success) {
        console.warn('⚠️ 后端API连接失败，请检查服务器状态');
      }
    });
  }

  // 获取认证头（纯Cookie认证）
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };
    return headers;
  }

  // 检查用户是否已登录（纯Cookie认证）
  isAuthenticated() {
    return cookieManager.isLoggedIn();
  }

  // 测试API连接
  async testApiConnection() {
    try {
      console.log('🔍 测试API连接:', `${this.baseURL}/health`);

      const response = await fetch(`${this.baseURL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // 添加Cookie支持
      });

      console.log('📡 API连接测试响应:', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type')
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ API连接成功:', data);
        return true;
      } else {
        console.warn('⚠️ API连接测试失败:', response.status);
        return false;
      }
    } catch (error) {
      console.error('❌ API连接测试错误:', error);
      return false;
    }
  }

  // 清理认证数据
  clearAuthData() {
    console.log('🧹 清理认证数据');
    cookieManager.clearAuthCookies();
    // 刷新页面重新登录
    setTimeout(() => {
      window.location.href = '/auth/login';
    }, 2000);
  }

  // 统一的响应处理方法
  async handleResponse(response, operation = '操作') {
    if (!response.ok) {
      if (response.status === 401) {
        console.log(`🚨 ${operation}收到401错误，清理认证信息`);
        this.clearAuthData();
        throw new Error('认证失败，请重新登录');
      }

      let errorMessage = `${operation}失败: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch (e) {
        // 如果无法解析错误响应，使用默认消息
        console.warn('无法解析错误响应:', e);
      }

      throw new Error(errorMessage);
    }

    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        // 如果不是JSON响应，返回文本内容
        const text = await response.text();
        console.warn('收到非JSON响应:', text);
        throw new Error(`${operation}失败: 服务器返回了非JSON格式的响应`);
      }
    } catch (e) {
      console.error('解析响应失败:', e);
      throw new Error(`${operation}失败: 无法解析服务器响应`);
    }
  }

  // 获取用户所有表单（包含完整数据）
  async getUserAllForms(includeData = true) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10秒超时

      let response;
      try {
        response = await fetch(
          `${this.baseURL}/user/forms?include_data=${includeData}`,
          {
            method: 'GET',
            headers: this.getAuthHeaders(),
            signal: controller.signal,
            credentials: 'include' // 添加Cookie支持
          }
        );

        clearTimeout(timeoutId);
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('请求超时，请检查网络连接');
        }
        throw error;
      }
      const data = await this.handleResponse(response, '获取用户表单');
      console.log('✅ 成功解析响应数据:', data);

      return {
        success: true,
        data: data.forms || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('❌ 获取用户表单失败:', error);
      console.error('错误详情:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  // 获取特定表单（包含完整数据）
  async getSpecificForm(formId) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }
      const response = await fetch(
        `${this.baseURL}/user/forms/${formId}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
          credentials: 'include', // 添加Cookie支持
        }
      );
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('表单不存在');
        }
        if (response.status === 401) {
          throw new Error('认证失败，请重新登录');
        }
        throw new Error(`获取表单失败: ${response.status}`);
      }
      // NOTE: 这里获取到了完整表单
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('获取特定表单失败:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // 删除表单记录
  async deleteForm(formId) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }
      const requestData = {
        id: formId
      }
      const response = await fetch(
        `${this.baseURL}/user/forms`,
        {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(requestData),
          credentials: 'include' // 添加Cookie支持
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('表单不存在');
        }
        if (response.status === 401) {
          throw new Error('认证失败，请重新登录');
        }
        throw new Error(`删除表单失败: ${response.status}`);
      }

      return {
        success: true,
        message: '表单删除成功'
      };
    } catch (error) {
      console.error('删除表单失败:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 搜索表单记录
  async searchForms(params = {}) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }

      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append('status', params.status);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.q) queryParams.append('q', params.q); // 添加查询关键字参数

      const response = await fetch(
        `${this.baseURL}/user/forms/search?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
          credentials: 'include' // 添加Cookie支持
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('认证失败，请重新登录');
        }
        throw new Error(`搜索表单失败: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data.forms || [],
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 10
      };
    } catch (error) {
      console.error('搜索表单失败:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  // 获取用户统计信息
  async getUserStats() {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }

      const response = await fetch(
        `${this.baseURL}/user/stats`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
          credentials: 'include' // 添加Cookie支持
        }
      );

      const data = await this.handleResponse(response, '获取统计信息');
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('获取统计信息失败:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // 保存匿名表单数据（用于未登录用户）
  async saveAnonymousForm(formData) {
    try {
      // 计算完成数
      const forms = formData || [];
      const completedForms = forms.filter(form => form.is_completed);
      const completionCount = completedForms.length;
      const totalForms = forms.length;
      const completionRate = totalForms > 0 ? ((completionCount / totalForms) * 100).toFixed(1) : '0.0';

      const requestData = {
        forms: formData,
        completion_stats: {
          total_forms: totalForms,
          completed_forms: completionCount,
          completion_rate: completionRate,
          completed_form_ids: completedForms.map(form => form.form_id),
          completed_form_titles: completedForms.map(form => form.form_title)
        }
      };

      console.log('📊 保存匿名表单时包含完成统计:', requestData.completion_stats);

      const response = await fetch(
        `${this.baseURL}/forms/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData),
          credentials: 'include' // 添加Cookie支持
        }
      );

      if (!response.ok) {
        throw new Error(`保存表单失败: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        userId: data.user_id,
        submissionId: data.submission_id
      };
    } catch (error) {
      console.error('保存匿名表单失败:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 保存表单数据到数据库（不触发验证码，用于页面切换时自动保存）
  async saveFormDataToDatabase(formData, formId = null) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }

      // 如果提供了formId，则更新现有表单
      if (formId) {
        return await this.updateForm(formId, formData);
      } else {
        // 如果没有提供formId，则创建新表单
        return await this.createForm(formData);
      }
    } catch (error) {
      console.error('保存表单数据到数据库失败:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 自动保存当前表单数据到数据库（页面切换时调用）
  async autoSaveCurrentFormData(formData) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }

      // 获取当前正在编辑的表单ID（从sessionStorage）
      const currentEditingFormId = formDataStorage.loadFormData('form_id');

      if (!formData) {
        console.log('📝 没有数据需要保存');
        return {
          success: true,
          message: '没有数据需要保存'
        };
      }

      // 保存到数据库
      const result = await this.saveFormDataToDatabase(formData, currentEditingFormId);

      if (result.success) {
        // 如果是新创建的表单，保存表单ID和标题到sessionStorage
        if (!currentEditingFormId && result.objectId) {
          formDataStorage.saveFormData('_id', result.objectId);
          formDataStorage.saveFormData('title', result.data?.title || '正在编辑的表单');
          console.log('✅ 新表单已创建，ObjectId:', result.objectId);
        }

        console.log('✅ 表单数据自动保存成功');
      } else {
        console.error('❌ 表单数据自动保存失败:', result.error);
      }

      return result;
    } catch (error) {
      console.error('❌ 自动保存表单数据失败:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 获取用户个人信息
  async getUserProfile() {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }

      const response = await fetch(
        `${this.baseURL}/user/profile`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
          credentials: 'include' // 添加Cookie支持
        }
      );

      const data = await this.handleResponse(response, '获取用户信息');
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // 更新用户个人信息
  async updateUserProfile(profileData) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }

      const response = await fetch(
        `${this.baseURL}/user/profile`,
        {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(profileData),
          credentials: 'include' // 添加Cookie支持
        }
      );

      const data = await this.handleResponse(response, '更新用户信息');
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('更新用户信息失败:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 创建新表单并获取ObjectId
  async createForm(formData) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }

      // 添加详细的控制台输出
      console.log('🚀 [CreateForm] 开始创建新表单');
      console.log('📊 [CreateForm] 原始表单数据:', formData);
      console.log('🔗 [CreateForm] 请求URL:', `${this.baseURL}/user/forms`);
      console.log('🔑 [CreateForm] 请求头:', this.getAuthHeaders());
      console.log('📤 [CreateForm] 发送到后端的完整数据:', JSON.stringify(formData, null, 2));

      const response = await fetch(
        `${this.baseURL}/user/forms`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(formData),
          credentials: 'include' // 添加Cookie支持
        }
      );

      console.log('📥 [CreateForm] 后端响应状态:', response.status, response.statusText);
      console.log('📥 [CreateForm] 响应头:', Object.fromEntries(response.headers.entries()));

      const data = await this.handleResponse(response, '创建表单');

      console.log('✅ [CreateForm] 后端返回数据:', data);
      console.log('🎉 [CreateForm] 表单创建成功，ObjectId:', data._id);

      return {
        ...data
      };
    } catch (error) {
      console.error('❌ [CreateForm] 创建表单失败:', error);
      console.error('❌ [CreateForm] 错误详情:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 使用ObjectId更新表单数据
  async updateForm(objectId, updateData) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录');
      }
      // 验证ObjectId格式
      if (!isValidObjectId(objectId)) {
        throw new Error('无效的ObjectId格式');
      }
      updateData.id = objectId;
      let requestData = {
        ...formDataStorage.getAllFormData(),
        ...updateData,
      };
      
      // 使用进度条的计算逻辑
      const formStatuses = getAllFormStatuses();
      
      // 获取签证类型
      const travelData = formDataStorage.loadFormData('travel');
      let visaType = '';
      if (travelData?.purpose?.length > 0) {
        const firstPurpose = travelData.purpose[0];
        if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F1-')) {
          visaType = 'F';
        } else if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F2-')) {
          visaType = 'F2';
        } else {
          visaType = firstPurpose.main_type || '';
        }
      }
      
      // 根据签证类型获取需要完成的表单页面
      const requiredPages = getFormPagesForVisaType(visaType);
      
      // 计算完成统计（使用进度条逻辑）
      const completedForms = Object.entries(formStatuses)
        .filter(([pageId, status]) => {
          const isRequiredPage = requiredPages.some(page => page.id === pageId);
          return isRequiredPage && status === 'completed';
        })
        .map(([pageId]) => {
          const page = requiredPages.find(p => p.id === pageId);
          return {
            id: pageId,
            title: page?.title || pageId,
            form_id: pageId,
            form_title: page?.title || pageId
          };
        });

      const totalForms = requiredPages.length;
      const completionCount = completedForms.length;
      const completionRate = totalForms > 0 ? ((completionCount / totalForms) * 100).toFixed(1) : '0.0';

      requestData = {
        ...requestData,
        id: objectId,
        completion_stats: {
          total_forms: totalForms,
          completed_forms: completionCount,
          completion_rate: completionRate,
          completed_form_ids: completedForms.map(form => form.form_id),
          completed_form_titles: completedForms.map(form => form.form_title)
        }
      };
      
      const response = await fetch(
        `${this.baseURL}/user/forms`,
        {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(requestData),
          credentials: 'include' // 添加Cookie支持
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('表单不存在');
        }
        if (response.status === 401) {
          throw new Error('认证失败，请重新登录');
        }
        if (response.status === 400) {
          throw new Error('无效的ObjectId格式');
        }
        const errorText = await response.text();
        throw new Error(`更新表单失败: ${response.status} - ${errorText}`);
      }

      return {
        success: true,
        data: formDataStorage.getAllFormData(),
        objectId: objectId
      };
    } catch (error) {
      console.error('更新表单失败:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 表单ID管理已迁移到formDataStorage，这些方法已移除
  // 使用 formDataStorage.loadFormData('form_id') 获取表单ID
  // 使用 formDataStorage.saveFormData('form_id', objectId) 保存表单ID

  // 格式化表单数据用于显示
  formatFormForDisplay(formData) {
    if (!formData) {
      return {
        title: '未知表单',
        status: 'unknown',
        progress: 0,
        completedPages: []
      };
    }

    console.log('formManageMentService.formatFormForDisplay:', formData)
    
    // 安全地获取 completion_stats，提供默认值
    const completionStats = formData.completion_stats || {
      total_forms: 0,
      completed_forms: 0,
      completion_rate: '0.0',
      completed_form_ids: [],
      completed_form_titles: []
    };
    
    // 确定状态
    let status = 'draft';
    if (completionStats.completed_forms === completionStats.total_forms && completionStats.total_forms > 0) {
      status = 'completed';
    } else if (completionStats.completed_forms > 0) {
      status = 'in_progress';
    }

    return {
      title: formData.title || '未命名申请',
      status: status,
      progress: completionStats.completed_forms,
      lastModified: formData.updated_at,
      createdDate: formData.created_at
    };
  }

  // 获取状态文本
  getStatusText(status) {
    const statusMap = {
      'completed': '已完成',
      'in_progress': '进行中',
      'draft': '草稿',
      'pending': '待处理',
      'processing': '处理中',
      'queued': '排队中',
      'failed': '失败',
      'unknown': '未知'
    };
    return statusMap[status] || '未知';
  }

  // 获取状态样式
  getStatusStyles(status) {
    const styles = {
      'completed': {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-200',
        icon: '✅'
      },
      'in_progress': {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-200',
        icon: '⏳'
      },
      'draft': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-200',
        icon: '📝'
      },
      'pending': {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-200',
        icon: '⏰'
      },
      'processing': {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        border: 'border-purple-200',
        icon: '🔄'
      },
      'queued': {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-200',
        icon: '🗂️'
      },
      'failed': {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-200',
        icon: '❌'
      },
      'unknown': {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-200',
        icon: '❓'
      }
    };
    return styles[status] || styles.unknown;
  }

  // 保存所有表单数据到数据库
  async saveAllFormData() {
    try {
      // 检查认证状态
      if (!this.isAuthenticated()) {
        throw new Error('用户未登录，无法保存数据');
      }

      // 获取当前正在编辑的表单ID和标题（从sessionStorage）
      const currentEditingFormId = formDataStorage.loadFormData('_id');
      const currentEditingFormTitle = formDataStorage.loadFormData('title');

      // 获取签证类型以确定需要哪些表单页面
      const travelData = formDataStorage.loadFormData('travel');
      let visaType = null;
      
      // 检查签证类型
      if (travelData?.purpose?.length > 0) {
        const firstPurpose = travelData.purpose[0];
        if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F1-')) {
          visaType = 'F';
        } else if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F2-')) {
          visaType = 'F2';
        }
      }
      
      console.log('🔍 formManagementService.saveAllFormData - 签证类型:', visaType);
      
      // 根据签证类型获取需要完成的表单页面
      const requiredPages = getFormPagesForVisaType(visaType);
      
      console.log('🔍 formManagementService.saveAllFormData - 页面列表:', {
        requiredPages: requiredPages.map(p => p.id),
        requiredPagesCount: requiredPages.length
      });
      
      // 使用进度条的计算逻辑获取表单状态
      const formStatuses = getAllFormStatuses();
      
      // 计算完成统计（使用进度条逻辑）
      const completedForms = Object.entries(formStatuses)
        .filter(([pageId, status]) => {
          // 只统计当前签证类型需要的页面
          const isRequiredPage = requiredPages.some(page => page.id === pageId);
          return isRequiredPage && status === 'completed';
        })
        .map(([pageId]) => {
          const page = requiredPages.find(p => p.id === pageId);
          return {
            id: pageId,
            title: page?.title || pageId,
            form_id: pageId,
            form_title: page?.title || pageId
          };
        });

      const totalForms = requiredPages.length;
      const completionCount = completedForms.length;
      const completionRate = totalForms > 0 ? ((completionCount / totalForms) * 100).toFixed(1) : '0.0';

      // 收集所有表单数据
      const { collectAllFormData } = await import('../utils/dataSubmitter.js');
      const allFormData = collectAllFormData();

      // 构造请求数据，使用进度条计算的 completion_stats
      const requestData = {
        ...allFormData,
        title: currentEditingFormTitle || '未命名申请',
        status: 'draft',
        completion_stats: {
          total_forms: totalForms,
          completed_forms: completionCount,
          completion_rate: completionRate,
          completed_form_ids: completedForms.map(form => form.form_id),
          completed_form_titles: completedForms.map(form => form.form_title)
        }
      };
      let result;
      if (currentEditingFormId) {
        // 如果有表单ID，则更新现有表单
        result = await this.updateForm(currentEditingFormId, requestData);
        console.log("result", result)
      } else {
        // 如果没有表单ID，则创建新表单
        result = await this.createForm(requestData);
      }

      if (result.success) {
        return {
          success: true,
          message: '所有表单数据已成功保存',
          data: result.data,
          objectId: result.objectId,
          title: requestData.title
        };
      } else {
        throw new Error(result.error || '保存失败');
      }

    } catch (error) {
      console.error('❌ 保存所有表单数据失败:', error);
      return {
        success: false,
        message: `保存失败: ${error.message}`,
        error: error.message
      };
    }
  }

  // （已移除）临时数据存储相关方法，改由 formDataStorage 提供
}

// 创建单例实例
const formManagementService = new FormManagementService();

// 便捷函数导出 - 绑定this上下文
export const getUserAllForms = formManagementService.getUserAllForms.bind(formManagementService);
export const getSpecificForm = formManagementService.getSpecificForm.bind(formManagementService);
export const updateForm = formManagementService.updateForm.bind(formManagementService);
export const deleteForm = formManagementService.deleteForm.bind(formManagementService);
export const searchForms = formManagementService.searchForms.bind(formManagementService);
export const getUserStats = formManagementService.getUserStats.bind(formManagementService);
export const saveAnonymousForm = formManagementService.saveAnonymousForm.bind(formManagementService);
export const getUserProfile = formManagementService.getUserProfile.bind(formManagementService);
export const updateUserProfile = formManagementService.updateUserProfile.bind(formManagementService);
export const createForm = formManagementService.createForm.bind(formManagementService);
export const formatFormForDisplay = formManagementService.formatFormForDisplay.bind(formManagementService);
export const getStatusText = formManagementService.getStatusText.bind(formManagementService);
export const getStatusStyles = formManagementService.getStatusStyles.bind(formManagementService);
export const isAuthenticated = formManagementService.isAuthenticated.bind(formManagementService);
export const saveFormDataToDatabase = formManagementService.saveFormDataToDatabase.bind(formManagementService);
export const autoSaveCurrentFormData = formManagementService.autoSaveCurrentFormData.bind(formManagementService);
export const saveAllFormData = formManagementService.saveAllFormData.bind(formManagementService);

export default formManagementService;
